import Box from '@mui/material/Box';
import { alpha, Theme, useTheme } from '@mui/material/styles';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { SignupBreakdownChartSeries } from 'src/interfaces/SignupBreakdown';

ChartJS.register(BarElement, CategoryScale, LinearScale, Legend, Tooltip);

interface Props {
  series: SignupBreakdownChartSeries;
  height?: number;
}

const STACK_COLORS = (palette: Theme['palette']) => [
  palette.primary.main,
  palette.secondary.main,
  palette.success.main,
  palette.warning.main,
  palette.info.main,
  palette.error.main,
];

const SignupBreakdownChart = ({ series, height = 280 }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const colors = useMemo(() => STACK_COLORS(theme.palette), [theme.palette]);

  const data = useMemo(
    () => ({
      labels: series.labels,
      datasets: series.datasets.map((dataset, index) => ({
        label: dataset.label,
        data: dataset.data,
        backgroundColor: colors[index % colors.length],
        borderRadius: 4,
        stack: 'signups',
      })),
    }),
    [colors, series.datasets, series.labels],
  );

  const axisColor = theme.palette.text.secondary;
  const gridColor = alpha(theme.palette.text.primary, 0.12);

  return (
    <Box sx={{ height, width: '100%' }}>
      <Bar
        options={{
          maintainAspectRatio: false,
          responsive: true,
          plugins: {
            legend: {
              display: series.datasets.length > 1,
              position: 'bottom',
              labels: { color: axisColor },
            },
            tooltip: {
              callbacks: {
                label: context => {
                  const label = context.dataset.label ?? '';
                  const value = context.parsed.y ?? 0;
                  return `${label}: ${value}`;
                },
                footer: items => {
                  const total = items.reduce((sum, item) => sum + (item.parsed.y ?? 0), 0);
                  return t('signups.breakdown.tooltipTotal', { count: total });
                },
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              ticks: { color: axisColor },
              grid: { color: gridColor },
            },
            y: {
              stacked: true,
              beginAtZero: true,
              ticks: {
                color: axisColor,
                precision: 0,
              },
              grid: { color: gridColor },
            },
          },
        }}
        data={data}
      />
    </Box>
  );
};

export default SignupBreakdownChart;
